import { STEP_INFO, TRAINER_STEPS, nextStep, type TrainerStep } from "./steps";

export type TrainerListener = (step: TrainerStep) => void;

/**
 * Central trainer lifecycle: step gating, completion, and a reset registry.
 *
 * Systems (XR input, environment placement, hands/grab) register reset hooks.
 * Calling {@link Trainer.reset} clears XR/input state first, then placement /
 * completion state via those hooks, and returns to the first step.
 *
 * Domain logic for wires/receptacle lives elsewhere; this module only gates
 * progress and broadcasts stage changes (e.g. to the HUD).
 */
export class Trainer {
  private step: TrainerStep = TRAINER_STEPS[0];
  private readonly resetHooks: Array<() => void> = [];
  private readonly listeners = new Set<TrainerListener>();

  getStep(): TrainerStep {
    return this.step;
  }

  getInfo() {
    return STEP_INFO[this.step];
  }

  isComplete(): boolean {
    return this.step === "complete";
  }

  /** Whether the given step is exactly the current gated stage. */
  isCurrent(step: TrainerStep): boolean {
    return this.step === step;
  }

  /**
   * True when `step` has been reached or passed. Use to ignore out-of-order
   * actions or to reveal later-stage affordances.
   */
  hasReached(step: TrainerStep): boolean {
    return TRAINER_STEPS.indexOf(this.step) >= TRAINER_STEPS.indexOf(step);
  }

  subscribe(listener: TrainerListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /**
   * Register work that must run on trainer reset (placement, completion flags,
   * held-object release, etc.). Returns an unregister function.
   */
  onReset(hook: () => void): () => void {
    this.resetHooks.push(hook);
    return () => {
      const i = this.resetHooks.indexOf(hook);
      if (i >= 0) this.resetHooks.splice(i, 1);
    };
  }

  /**
   * Advance from `from` only if it is the current step. Idempotent no-op
   * otherwise — safe for environment/hands to call when their criteria succeed.
   */
  completeStep(from: TrainerStep): boolean {
    if (this.step !== from) return false;
    const next = nextStep(from);
    if (!next) return false;
    this.setStep(next);
    return true;
  }

  /** Force a step (debug / specialist tooling). Prefer {@link completeStep}. */
  setStep(step: TrainerStep): void {
    if (this.step === step) return;
    this.step = step;
    for (const listener of this.listeners) listener(this.step);
  }

  /**
   * Outlet-station reset entry point. Clears registered systems then returns to
   * the first stage. Call from the station module / reset plate — not as a
   * global workshop wipe. Session start/end should release holds only.
   */
  reset(): void {
    for (const hook of this.resetHooks) {
      try {
        hook();
      } catch (err) {
        console.error("[Trainer] reset hook failed", err);
      }
    }
    this.step = TRAINER_STEPS[0];
    for (const listener of this.listeners) listener(this.step);
  }
}
